import { format, parseISO } from "date-fns";
import type { ChildImmunisationRegisterQuery } from "../__generated__/page.gql";

type ChildImmunisationRecord = NonNullable<
  NonNullable<
    NonNullable<
      ChildImmunisationRegisterQuery["childImmunisationRegister"]
    >["records"]
  >[number]
>;

export type RegisterRow = {
  patientId: string;
  name: string;
  dateOfBirth: string;
  gender: string;
  address: string;
  phone: string;
  "HEP B 0": string;
  BCG: string;
  "OPV 0": string;
  "PENTA 1": string;
  "PCV 1": string;
  "ROTA 1": string;
  "OPV 1": string;
  "IPV 1": string;
  "PENTA 2": string;
  "PCV 2": string;
  "ROTA 2": string;
  "OPV 2": string;
  "PENTA 3": string;
  "PCV 3": string;
  "ROTA 3": string;
  "OPV 3": string;
  "IPV 2": string;
  "VITAMIN A 1": string;
  "VITAMIN A 2": string;
  "MEASLES 1": string;
  "YELLOW FEVER": string;
  "MENINGITIS A": string;
  "MEASLES 2": string;
  HPV: string;
};

// Cache for formatted dates to avoid repeated parsing and formatting
const dateCache = new Map<
  string,
  { formattedDate: string; hasVaccination: boolean }
>();

const formatDate = (
  dateString: string | null | undefined,
): { formattedDate: string; hasVaccination: boolean } => {
  if (!dateString) return { formattedDate: "", hasVaccination: false };

  // Check cache first
  const cached = dateCache.get(dateString);
  if (cached) return cached;

  try {
    const result = {
      formattedDate: format(parseISO(dateString), "dd MMM yyyy"),
      hasVaccination: true,
    };
    dateCache.set(dateString, result);
    return result;
  } catch {
    return { formattedDate: "", hasVaccination: false };
  }
};

// Pre-define the vaccination field mappings to avoid object creation on each record
const VACCINATION_FIELD_MAPPINGS = {
  "HEP B 0": "hepatitisB0",
  BCG: "bcg",
  "OPV 0": "opv0",
  "PENTA 1": "penta1",
  "PCV 1": "pcv1",
  "ROTA 1": "rota1",
  "OPV 1": "opv1",
  "IPV 1": "ipv1",
  "PENTA 2": "penta2",
  "PCV 2": "pcv2",
  "ROTA 2": "rota2",
  "OPV 2": "opv2",
  "PENTA 3": "penta3",
  "PCV 3": "pcv3",
  "ROTA 3": "rota3",
  "OPV 3": "opv3",
  "IPV 2": "ipv2",
  "VITAMIN A 1": "vitaminA1",
  "VITAMIN A 2": "vitaminA2",
  "MEASLES 1": "measles1",
  "YELLOW FEVER": "yellowFever",
  "MENINGITIS A": "meningitisA",
  "MEASLES 2": "measles2",
  HPV: "hpv",
} as const;

// Pre-define empty vaccination fields to avoid object creation on each record
const EMPTY_VACCINATION_FIELDS = Object.fromEntries(
  Object.keys(VACCINATION_FIELD_MAPPINGS).map((key) => [key, ""]),
) as Record<keyof typeof VACCINATION_FIELD_MAPPINGS, string>;

export const mapGqlPatientToVaccinationDates = (
  record: ChildImmunisationRecord,
): { result: RegisterRow; hasAtLeastOneVaccination: boolean } => {
  let hasAtLeastOneVaccination = false;

  // Get address parts once
  const addressParts = [
    record.patient.person.addresses[0]?.street,
    record.patient.person.addresses[0]?.ward,
    record.patient.person.addresses[0]?.subCounty,
    record.patient.person.addresses[0]?.state,
    record.patient.person.addresses[0]?.country,
  ].filter(Boolean);

  const result: RegisterRow = {
    patientId: record.patient.id,
    name: record.patient.person.name.fullname ?? "",
    dateOfBirth: formatDate(record.patient.person.dob).formattedDate,
    gender: record.patient.person.gender.toUpperCase(),
    address:
      addressParts.length > 0
        ? `${addressParts.join(", ").slice(0, 30)}...`
        : "",
    phone: record.patient.guardians?.[0]?.phone?.number ?? "",
    ...EMPTY_VACCINATION_FIELDS,
  };

  // Process vaccination fields
  for (const [displayKey, recordKey] of Object.entries(
    VACCINATION_FIELD_MAPPINGS,
  )) {
    const value = record[recordKey as keyof typeof record];
    const { formattedDate, hasVaccination } = formatDate(
      value as string | null | undefined,
    );
    result[displayKey as keyof RegisterRow] = formattedDate;
    if (hasVaccination) hasAtLeastOneVaccination = true;
  }

  return { result, hasAtLeastOneVaccination };
};
